import styled from '@emotion/styled'
import { LogoFull } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { Media } from 'app/theme/media'
import { useVocabularyData } from 'app/utils/vocabulary'
import { compact } from 'lodash'
import React, { memo, useContext } from 'react'
import { isMobile } from 'react-device-detect'

import { FooterContext } from './index'
import { MenuItem } from './MenuItem'
import { Social } from './Social'

export const Contacts = memo(function Contacts() {
  const {
    address,
    email,
    languageCode,
    menuItems,
    phone,
    phone2,
    phone3,
    phone4,
  } = useContext(FooterContext) as any

  return (
    <Container row space="between" wrap>
      <Column>
        <LogoFull />
      </Column>

      <Column>
        <Label>{useVocabularyData('hotel', languageCode)}</Label>

        {address ? (
          <Anchor
            dangerouslySetInnerHTML={{ __html: address }}
            href={`https://maps.google.com/?q=${address}`}
            rel="noreferrer"
            target="_blank"
          />
        ) : null}

        {phone ? (
          <>
            <Separator />

            <Anchor
              href={`tel:${phone}`}
              onClick={() => {
                if (isMobile) {
                  typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: 'Phone Number',
                    })
                }
              }}
            >
              {phone}
            </Anchor>
          </>
        ) : null}

        {email ? (
          <>
            <Separator />

            <Anchor
              href={`mailto:${email}`}
              onClick={() => {
                typeof window.gtag !== 'undefined' &&
                  window.gtag('event', 'Click', {
                    event_category: 'Website',
                    event_label: 'Email Address',
                  })
              }}
            >
              {email}
            </Anchor>
          </>
        ) : null}
      </Column>

      <Media lessThan="desktopSmall">
        {(className: any, renderChildren: any) => {
          return renderChildren ? (
            <Column className={className}>
              <Label>{useVocabularyData('social', languageCode)}</Label>

              <Social />
            </Column>
          ) : null
        }}
      </Media>

      <Column>
        {phone2 ? (
          <>
            <Label>A Spurcacciun-a</Label>

            <Anchor
              href={`tel:${phone2}`}
              onClick={() => {
                if (isMobile) {
                  typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: 'Phone Number',
                    })
                }
              }}
            >
              {phone2}
            </Anchor>
          </>
        ) : null}

        {phone3 ? (
          <>
            <Label className="pad">Bistrot Marea</Label>

            <Anchor
              href={`tel:${phone3}`}
              onClick={() => {
                if (isMobile) {
                  typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: 'Phone Number',
                    })
                }
              }}
            >
              {phone3}
            </Anchor>
          </>
        ) : null}

        {phone4 ? (
          <>
            <Label className="pad">Sushi Beach</Label>

            <Anchor
              href={`tel:${phone4}`}
              onClick={() => {
                if (isMobile) {
                  typeof window.gtag !== 'undefined' &&
                    window.gtag('event', 'Click', {
                      event_category: 'Website',
                      event_label: 'Phone Number',
                    })
                }
              }}
            >
              {phone4}
            </Anchor>
          </>
        ) : null}
      </Column>

      <Column>
        <Label>{useVocabularyData('stay', languageCode)}</Label>

        {menuItems ? (
          <Nav>
            {menuItems.map((item: any, index: any) => (
              <Li key={index}>
                <MenuItem {...item} />
              </Li>
            ))}
          </Nav>
        ) : null}
      </Column>

      <Media greaterThanOrEqual="desktopSmall">
        {(className: any, renderChildren: any) => {
          return renderChildren ? (
            <Column className={className}>
              <Label>{useVocabularyData('social', languageCode)}</Label>

              <Social />
            </Column>
          ) : null
        }}
      </Media>
    </Container>
  )
})

const Container = styled(FlexBox)`
  padding: 6rem 5vw;

  @media (max-width: 1199px) {
    display: block;
    padding: 3.75rem 2rem;
  }
`

const Column = styled.div`
  &:nth-of-type(2),
  &:nth-of-type(3) {
    min-width: 20vw;
  }
  &:nth-of-type(4),
  &:nth-of-type(5) {
    min-width: 12.222vw;
  }

  > svg {
    width: 7rem;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralLight1};
  }

  @media (max-width: 1199px) {
    margin-top: 3rem;
    &:first-of-type {
      display: none;
    }
    &:nth-of-type(2) {
      margin-top: 0;
    }
    &:nth-of-type(2),
    &:nth-of-type(3),
    &:nth-of-type(4),
    &:nth-of-type(5) {
      min-width: auto;
    }
  }
`

const Label = styled.div`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  margin-bottom: 1.5rem;
  &.pad {
    margin-top: 3.375rem;
  }

  @media (max-width: 1199px) {
    margin-bottom: 0.75rem;
    &.pad {
      margin-top: 3rem;
    }
  }
`

const Anchor = styled.a`
  ${styles.description}

  display: inline-block;
  color: ${({ theme }) => theme.colors.variants.neutralLight1};
  position: relative;
  &:after {
    content: '';
    width: 100%;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight1};
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.8s cubic-bezier(0.24, 0.43, 0.15, 0.97);
  }
  &:hover {
    &:after {
      transform: scaleX(1);
      transform-origin: left;
    }
  }
`

const Separator = styled.div`
  margin-top: 0.75rem;
`

const Nav = styled.ul``

const Li = styled.li`
  margin-top: 1rem;
  &:first-of-type {
    margin-top: 0;
  }
`
